import { Grid, Text, Stack, Input, Popover, Box } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { getUserCountry } from 'utils/i18n';
import { CenteredLoading } from 'components/CenteredLoading';
import { PaperHeader } from 'components/PaperHeader/PaperHeader';
import { DebouncedInput } from 'components/ControlledInputs/DebouncedInput';
import { useProfile, useUpdateCustomField } from 'hooks/api/useProfile';
import { PatientIdProps } from 'models/general';
import { usePatient } from 'hooks/api/useUser';
import { ProfileCustomFields } from 'models/profile';
import { useState } from 'react';

export const PatientPersonalInfo = ({ patientId }: PatientIdProps) => {
  const { t } = useTranslation();
  const [isEditingPatientId, setIsEditingPatientId] = useState(false);
  const { data: patientData, isLoading } = usePatient({ patientId });
  const { data: patientProfile, isLoading: isLoadingProfile } = useProfile(patientId);
  const { mutate: changePatientId } = useUpdateCustomField({
    customField: ProfileCustomFields.PatientId,
    fieldLabel: t('patients_group_id_label'),
  });

  if (isLoading || isLoadingProfile) return <CenteredLoading />;

  return (
    <>
      <PaperHeader
        titleKey={'patients_personal_info'}
        iconKey={'patientdetails_personalhealthinfo_personalinfo_tooltip'}
      />
      <Stack mt={20}>
        <Grid align={'center'}>
          <Grid.Col span={5}>
            <Text>{t('patients_group_id_label')}</Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <Popover width={'target'} position={'bottom'} shadow={'md'} opened={isEditingPatientId}>
              <Popover.Target>
                <Box onKeyDown={() => setIsEditingPatientId(true)} onBlur={() => setIsEditingPatientId(false)}>
                  <DebouncedInput
                    onChange={(value) => changePatientId({ profileId: patientData?.id, value })}
                    value={patientProfile?.customFields?.extraPatientId}
                  />
                </Box>
              </Popover.Target>
              <Popover.Dropdown>
                <Text size={'sm'}>{t('patients_group_id_tooltip')}</Text>
              </Popover.Dropdown>
            </Popover>
          </Grid.Col>
          <Grid.Col span={5}>
            <Text>{t('patients_group_first_name_label')}</Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <Input defaultValue={patientData?.firstName} name={'firstname'} disabled />
          </Grid.Col>
          <Grid.Col span={5}>
            <Text>{t('patients_group_last_name_label')}</Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <Input defaultValue={patientData?.lastName} name={'lastname'} disabled />
          </Grid.Col>
          <Grid.Col span={5}>
            <Text>{t('patients_group_email_label')}</Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <Input defaultValue={patientData?.email} name={'email'} disabled />
          </Grid.Col>
          <Grid.Col span={5}>
            <Text>{t('patients_group_phone_label')}</Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <Input defaultValue={patientData?.phoneNumber} name={'phoneNumber'} disabled />
          </Grid.Col>
          <Grid.Col span={5}>
            <Text>{t('patients_group_country_label')}</Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <Input value={getUserCountry(patientProfile?.country)} name={'country'} disabled />
          </Grid.Col>
        </Grid>
      </Stack>
    </>
  );
};
